/**
 * Every parent-facing string about "auto-reload can't fire, so your auto-order
 * can't run" — in ONE file.
 *
 * The PREDICATE is not here: the server derives it once
 * (`server/src/managers/balance-autoreload-policy.ts` +
 * `auto-order-autoreload-status.ts`) and ships it on
 * `GET /api/family-subscriptions` as `autoReloadAlerts` +
 * per-subscription `autoReloadState`. This module only turns that answer into
 * words, so Home, My Family, and Billing say consistent things about the same
 * state instead of three near-copies drifting apart.
 *
 * Copy rules baked in here:
 *   - The TITLE is the consequence (what is or isn't happening to the meals).
 *   - The BODY is the cause, and it names the REAL cause: "auto-reload is off"
 *     is a lie to a household that removed its default card.
 *   - Nothing claims the runner paused something unless the audit trail said
 *     so, and nothing says "will be skipped" about a row already paused.
 */

import type {
  AutoOrderAutoReloadAlert,
  AutoOrderAutoReloadState,
  AutoReloadDisarmedReason,
} from '@/api/family-subscriptions'

/** Copy for one alert surface (Home banner today). */
export interface AutoReloadAlertCopy {
  /** Consequence — what is happening to the meals. */
  title: string
  /** Cause — why auto-reload can't fire. */
  body: string
  /** Label for the button that opens the shared auto-reload dialog. */
  actionLabel: string
}

/**
 * The alert for a given account, or the first one when no account is named.
 *
 * Billing renders one balance card per household and must show only that
 * household's alert; Home has no account context and shows whatever is broken.
 */
export function selectAutoReloadAlert(
  alerts: readonly AutoOrderAutoReloadAlert[] | undefined,
  accountId?: string | null,
): AutoOrderAutoReloadAlert | null {
  const list = alerts ?? []
  if (accountId) {
    return list.find((alert) => alert.accountId === accountId) ?? null
  }
  return list[0] ?? null
}

/** How many auto-orders an alert covers. */
export function autoReloadAlertCount(alert: AutoOrderAutoReloadAlert): number {
  return alert.subscriptions.length
}

function everyState(alert: AutoOrderAutoReloadAlert, state: AutoOrderAutoReloadState): boolean {
  return alert.subscriptions.length > 0 && alert.subscriptions.every((sub) => sub.state === state)
}

/**
 * Consequence line. Only claims a single cause when every affected auto-order
 * shares it — a mixed set falls back to the one thing that is true of all of
 * them ("can't run").
 */
function alertTitle(alert: AutoOrderAutoReloadAlert): string {
  const n = alert.subscriptions.length
  const subject = n === 1 ? 'Your auto-order' : `Your ${n} auto-orders`
  if (everyState(alert, 'PAUSED_BY_AUTO_RELOAD')) {
    return n === 1 ? `${subject} is paused` : `${subject} are paused`
  }
  if (everyState(alert, 'AT_RISK')) {
    return `${subject} will be skipped`
  }
  if (everyState(alert, 'RESUME_BLOCKED')) {
    return `${subject} can't be resumed`
  }
  return `${subject} can't run`
}

/** Cause line — names the actual disarmed reason, never a generic "it's off". */
function alertBody(reason: AutoReloadDisarmedReason): string {
  const tail = 'Auto-orders are charged to that balance.'
  if (reason === 'NO_CARD') {
    return `Balance auto-reload has no default payment method, so your household balance can't refill itself. ${tail}`
  }
  if (reason === 'INCOMPLETE') {
    return `Balance auto-reload is missing a re-load amount or threshold, so your household balance can't refill itself. ${tail}`
  }
  return `Balance auto-reload is off, so your household balance can't refill itself. ${tail}`
}

/** "Turn on" is only honest when the switch is the thing that's off. */
function alertActionLabel(reason: AutoReloadDisarmedReason): string {
  return reason === 'DISABLED' ? 'Turn on auto-reload' : 'Set up auto-reload'
}

export function autoReloadAlertCopy(alert: AutoOrderAutoReloadAlert): AutoReloadAlertCopy {
  return {
    title: alertTitle(alert),
    body: alertBody(alert.disarmedReason),
    actionLabel: alertActionLabel(alert.disarmedReason),
  }
}

/**
 * The short warning line on a My Family auto-order row.
 *
 * Deliberately reason-agnostic ("needs auto-reload" is true whether it's off,
 * cardless, or half-configured) — the row has no room for the full cause, and
 * the detail page it links to spells it out.
 */
export function autoOrderStateWarning(state: AutoOrderAutoReloadState | undefined): string | null {
  switch (state) {
    case 'AT_RISK':
      return 'Auto-reload needed — will be skipped'
    case 'PAUSED_BY_AUTO_RELOAD':
      return 'Paused — auto-reload needed'
    case 'RESUME_BLOCKED':
      return 'Auto-reload needed to resume'
    default:
      return null
  }
}

/**
 * The consequence appended to the Billing page's auto-reload row, which
 * otherwise reads "Off — your balance will not auto-reload" and never mentions
 * that auto-orders depend on it. Null when nothing depends on it.
 */
export function autoReloadRowConsequence(alert: AutoOrderAutoReloadAlert | null): string | null {
  if (!alert || alert.subscriptions.length === 0) {
    return null
  }
  const n = alert.subscriptions.length
  return n === 1 ? "1 auto-order can't run without it" : `${n} auto-orders can't run without it`
}
